import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../App';
import api, { getErrorMessage } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { CurrencyDollar, TrendUp, TrendDown, Wallet, Receipt, FileXls, ShoppingCart, Trash, Plus, Printer, Download, X, CaretDown, CaretUp, ClockClockwise } from '@phosphor-icons/react';
import BarcodeScanButton from '../components/BarcodeScanButton';

const CompanyLedger = () => {
  const { activeBranch } = useBranch();
  const { user } = useAuth();
  const userRole = user?.role || 'staff';
  const [purchases, setPurchases] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkPaymentHistory, setBulkPaymentHistory] = useState([]);
  const [availableCredit, setAvailableCredit] = useState(0);
  
  // Date filter states
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [filteredPaymentTotal, setFilteredPaymentTotal] = useState(0);
  
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showBulkPaymentDialog, setShowBulkPaymentDialog] = useState(false);
  const [showBulkPaymentHistoryDialog, setShowBulkPaymentHistoryDialog] = useState(false);
  const [showEditPurchaseDialog, setShowEditPurchaseDialog] = useState(false);
  const [showDeletePurchaseDialog, setShowDeletePurchaseDialog] = useState(false);
  const [showEditBulkPaymentDialog, setShowEditBulkPaymentDialog] = useState(false);
  const [showDeleteBulkPaymentDialog, setShowDeleteBulkPaymentDialog] = useState(false);
  
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [expandedPurchase, setExpandedPurchase] = useState(null);
  const [purchaseToDelete, setPurchaseToDelete] = useState(null);
  const [selectedBulkPayment, setSelectedBulkPayment] = useState(null);
  const [bulkPaymentToDelete, setBulkPaymentToDelete] = useState(null);
  
  const [purchaseForm, setPurchaseForm] = useState({
    barcode: '',
    quantity: 1,
    price_per_unit: 0,
    paid_amount: 0,
    initial_payment_method: 'cash',
    supplier_name: ''
  });
  
  const [saleCart, setSaleCart] = useState([]);
  const [saleCustomer, setSaleCustomer] = useState('');
  const [salePayment, setSalePayment] = useState({
    amount_paid: 0,
    payment_method: 'cash'
  });
  const [currentItem, setCurrentItem] = useState({
    barcode: '',
    quantity: 1,
    price_per_unit: 0
  });
  
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    payment_method: 'cash',
    remarks: ''
  });
  
  const [bulkPaymentForm, setBulkPaymentForm] = useState({
    amount: 0,
    payment_method: 'cash',
    remarks: '',
    use_credit: false,
    date: ''
  });

  const PAYMENT_METHODS = [
    { value: 'cash', label: 'Cash' },
    { value: 'online', label: 'Online Transfer' },
    { value: 'upi', label: 'UPI' },
    { value: 'check', label: 'Cheque' },
    { value: 'card', label: 'Card' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (activeBranch) {
      fetchLedgerData();
      fetchCustomers();
    }
  }, [activeBranch, fromDate, toDate]);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers', {
        params: { branch_id: activeBranch?.branch_id }
      });
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };

  const setQuickDateRange = (range) => {
    const today = new Date();
    let from, to;

    switch (range) {
      case 'today':
        from = to = today.toISOString().split('T')[0];
        break;
      case 'this_week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        from = startOfWeek.toISOString().split('T')[0];
        to = today.toISOString().split('T')[0];
        break;
      case 'this_month':
        from = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        to = today.toISOString().split('T')[0];
        break;
      case 'last_month':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        from = lastMonth.toISOString().split('T')[0];
        to = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];
        break;
      case 'this_year':
        from = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        to = today.toISOString().split('T')[0];
        break;
      default:
        from = '';
        to = '';
    }

    setFromDate(from);
    setToDate(to);
    setTimeout(() => fetchLedgerData(), 100);
  };

  const fetchLedgerData = async () => {
    setLoading(true);
    try {
      const params = { branch_id: activeBranch.branch_id };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      
      const [transactionsRes, summaryRes, purchasesRes] = await Promise.all([
        api.get('/ledger/transactions', { params }),
        api.get('/ledger/summary', { params }),
        api.get('/ledger/purchases', { params })
      ]);
      
      const transactionsData = transactionsRes.data;
      setTransactions(transactionsData);
      setLedgerSummary(summaryRes.data);
      setPurchases(purchasesRes.data);
      
      // Calculate total payments in filtered period
      const totalPayments = transactionsData
        .filter(t => t.transaction_type === 'payment')
        .reduce((sum, t) => sum + t.payment_amount, 0);
      setFilteredPaymentTotal(totalPayments);
    } catch (error) {
      console.error('Ledger data fetch error:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied to Company Ledger');
      } else {
        toast.error(getErrorMessage(error, 'Failed to load ledger data'));
      }
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = async () => {
    setFromDate('');
    setToDate('');
    setFilteredPaymentTotal(0);
    
    // Fetch data without date filters
    setLoading(true);
    try {
      const params = { branch_id: activeBranch.branch_id };
      
      const [transactionsRes, summaryRes, purchasesRes] = await Promise.all([
        api.get('/ledger/transactions', { params }),
        api.get('/ledger/summary', { params }),
        api.get('/ledger/purchases', { params })
      ]);
      
      const transactionsData = transactionsRes.data;
      setTransactions(transactionsData);
      setLedgerSummary(summaryRes.data);
      setPurchases(purchasesRes.data);
      
      // Reset payment total to 0 when showing all transactions
      setFilteredPaymentTotal(0);
      
      toast.success('Filters cleared - showing all transactions');
    } catch (error) {
      console.error('Failed to clear filters:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/ledger/purchase', {
        ...purchaseForm,
        branch_id: activeBranch.branch_id
      });
      toast.success('Purchase recorded successfully! Stock updated automatically.');
      setShowPurchaseDialog(false);
      setPurchaseForm({ barcode: '', quantity: 1, price_per_unit: 0, paid_amount: 0, initial_payment_method: 'cash', supplier_name: '' });
      fetchLedgerData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Purchase failed'));
    }
  };

  // Auto-populate price when barcode changes
  const handleBarcodeChange = async (barcode) => {
    setPurchaseForm({...purchaseForm, barcode});
    
    if (barcode.length > 0) {
      try {
        const response = await api.get(`/stock/barcode/${barcode}`);
        if (response.data && response.data.purchase_price) {
          setPurchaseForm(prev => ({
            ...prev,
            barcode,
            price_per_unit: response.data.purchase_price
          }));
          toast.success(`Auto-filled price: ₹${response.data.purchase_price} from ${response.data.name}`);
        }
      } catch (error) {
        // Item not found or no price set - silently continue
        console.log('Barcode lookup:', error.response?.status === 404 ? 'Item not found' : 'Lookup failed');
      }
    }
  };

  const fetchBulkPaymentHistory = async () => {
    try {
      const response = await api.get('/ledger/bulk-payment-history', {
        params: { branch_id: activeBranch.branch_id }
      });
      setBulkPaymentHistory(response.data.history || []);
      setShowBulkPaymentHistoryDialog(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load bulk payment history'));
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/ledger/purchase/${selectedPurchase.purchase_id}/payment`, paymentForm);
      toast.success('Payment recorded successfully!');
      setShowPaymentDialog(false);
      setPaymentForm({ amount: 0, payment_method: 'cash', remarks: '' });
      setSelectedPurchase(null);
      fetchLedgerData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Payment failed'));
    }
  };

  const openPaymentDialog = (purchase) => {
    setSelectedPurchase(purchase);
    setPaymentForm({ amount: 0, payment_method: 'cash', remarks: '' });
    setShowPaymentDialog(true);
  };

  const togglePaymentHistory = (purchaseId) => {
    setExpandedPurchase(expandedPurchase === purchaseId ? null : purchaseId);
  };

  const openBulkPaymentDialog = () => {
    // Initialize form with outstanding balance
    setBulkPaymentForm({
      amount: ledgerSummary?.outstanding_payments || 0,
      payment_method: 'cash',
      remarks: '',
      use_credit: false
    });
    setShowBulkPaymentDialog(true);
  };

  const handleBulkPaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (!bulkPaymentForm.payment_method) {
      toast.error('Please select a payment method');
      return;
    }
    
    const amount = parseFloat(bulkPaymentForm.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid payment amount greater than 0');
      return;
    }
    
    try {
      const result = await api.post('/ledger/bulk-payment', {
        branch_id: activeBranch.branch_id,
        amount: parseFloat(bulkPaymentForm.amount),
        payment_method: bulkPaymentForm.payment_method,
        remarks: bulkPaymentForm.remarks,
        use_credit: bulkPaymentForm.use_credit
      });
      
      const cashPaid = result.data.cash_paid || 0;
      const creditUsed = result.data.credit_used || 0;
      const actualApplied = result.data.distributed_amount || result.data.total_amount;
      const msg = creditUsed > 0 
        ? `Successfully paid ₹${actualApplied.toLocaleString()} (Cash: ₹${cashPaid.toLocaleString()} + Credit: ₹${Math.min(creditUsed, actualApplied).toLocaleString()}) across ${result.data.purchases_updated.length} purchase(s)!`
        : `Successfully paid ₹${parseFloat(bulkPaymentForm.amount).toLocaleString()} across ${result.data.purchases_updated.length} purchase(s)!`;
      
      toast.success(msg);
      setShowBulkPaymentDialog(false);
      setBulkPaymentForm({ amount: 0, payment_method: 'cash', remarks: '', use_credit: false });
      setAvailableCredit(0);
      fetchLedgerData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to record bulk payment'));
    }
  };

  const openEditPurchase = (purchase) => {
    setPurchaseForm({
      barcode: purchase.barcode,
      quantity: purchase.quantity,
      price_per_unit: purchase.price_per_unit,
      paid_amount: purchase.amount_paid || 0,
      initial_payment_method: purchase.initial_payment_method || 'cash',
      supplier_name: purchase.supplier_name || '',
      purchase_date: purchase.purchase_date || new Date().toISOString().split('T')[0]
    });
    setSelectedPurchase(purchase);
    setShowEditPurchaseDialog(true);
  };

  const handleEditPurchaseSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/ledger/purchase/${selectedPurchase.purchase_id}`, {
        quantity: parseFloat(purchaseForm.quantity),
        price_per_unit: parseFloat(purchaseForm.price_per_unit),
        purchase_date: purchaseForm.purchase_date,
        supplier_name: purchaseForm.supplier_name
      });
      toast.success('Purchase updated successfully!');
      setShowEditPurchaseDialog(false);
      setSelectedPurchase(null);
      setPurchaseForm({ barcode: '', quantity: 1, price_per_unit: 0, paid_amount: 0, initial_payment_method: 'cash', supplier_name: '' });
      fetchLedgerData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update purchase'));
    }
  };

  const openDeletePurchase = (purchase) => {
    setPurchaseToDelete(purchase);
    setShowDeletePurchaseDialog(true);
  };

  const handleDeletePurchase = async () => {
    try {
      const response = await api.delete(`/ledger/purchase/${purchaseToDelete.purchase_id}`);
      if (response.data.stock_restored) {
        toast.success('Purchase deleted and stock removed from inventory!');
      } else {
        toast.success('Purchase deleted successfully!');
      }
      setShowDeletePurchaseDialog(false);
      setPurchaseToDelete(null);
      fetchLedgerData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete purchase'));
    }
  };

  const openEditBulkPayment = (transaction) => {
    // Check if this is a bulk payment object (has bulk_payment_id) or transaction object
    if (!transaction.bulk_payment_id) {
      toast.error('Please use the Bulk Payment History to edit bulk payments');
      return;
    }
    
    setSelectedBulkPayment(transaction);
    // Extract date from transaction (format: YYYY-MM-DD)
    const transactionDate = transaction.date || transaction.created_at ? 
      (transaction.date || transaction.created_at).split('T')[0] : 
      new Date().toISOString().split('T')[0];
    setBulkPaymentForm({
      amount: transaction.payment_amount || transaction.total_amount || 0,
      payment_method: transaction.payment_method || 'cash',
      remarks: transaction.remarks || '',
      date: transactionDate
    });
    setShowEditBulkPaymentDialog(true);
  };

  const handleEditBulkPaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/ledger/bulk-payment/${selectedBulkPayment.bulk_payment_id}`, {
        amount: parseFloat(bulkPaymentForm.amount),
        payment_method: bulkPaymentForm.payment_method,
        remarks: bulkPaymentForm.remarks,
        date: bulkPaymentForm.date
      });
      toast.success('Bulk payment updated successfully!');
      setShowEditBulkPaymentDialog(false);
      setSelectedBulkPayment(null);
      setBulkPaymentForm({ amount: 0, payment_method: 'cash', remarks: '', use_credit: false, date: '' });
      fetchLedgerData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update bulk payment'));
    }
  };

  const openDeleteBulkPayment = (transaction) => {
    // Check if this is a bulk payment object (has bulk_payment_id) or transaction object
    if (!transaction.bulk_payment_id) {
      toast.error('Please use the Bulk Payment History to delete bulk payments');
      return;
    }
    
    setBulkPaymentToDelete(transaction);
    setShowDeleteBulkPaymentDialog(true);
  };

  const handleDeleteBulkPayment = async () => {
    try {
      await api.delete(`/ledger/bulk-payment/${bulkPaymentToDelete.bulk_payment_id}`);
      toast.success('Bulk payment deleted successfully!');
      setShowDeleteBulkPaymentDialog(false);
      setBulkPaymentToDelete(null);
      fetchLedgerData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete bulk payment'));
    }
  };

  const exportToExcel = (data, filename) => {
    if (data.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(item => Object.values(item).map(val => `"${val}"`).join(','));
    const csv = [headers, ...rows].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported successfully!');
  };

  if (loading) {
    return (
      <Layout title="Company Ledger">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#2C4C3B] border-r-transparent"></div>
            <p className="mt-4 text-[#2C4C3B]">Loading ledger data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Company Ledger">
      <div className="space-y-6">
        {/* Date Filter */}
        <Card className="p-4 bg-white border-[#E5E5DF]">
          <div className="space-y-4">
            {/* Quick Date Range Buttons */}
            <div className="flex flex-wrap gap-2">
              <span className="text-sm font-medium text-[#2C4C3B] self-center mr-2">Quick Select:</span>
              <Button size="sm" variant="outline" onClick={() => setQuickDateRange('today')} className="text-xs">
                Today
              </Button>
              <Button size="sm" variant="outline" onClick={() => setQuickDateRange('this_week')} className="text-xs">
                This Week
              </Button>
              <Button size="sm" variant="outline" onClick={() => setQuickDateRange('this_month')} className="text-xs">
                This Month
              </Button>
              <Button size="sm" variant="outline" onClick={() => setQuickDateRange('last_month')} className="text-xs">
                Last Month
              </Button>
              <Button size="sm" variant="outline" onClick={() => setQuickDateRange('this_year')} className="text-xs">
                This Year
              </Button>
            </div>

            {/* Custom Date Range */}
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-sm font-medium text-[#2C4C3B]">From Date</Label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <Label className="text-sm font-medium text-[#2C4C3B]">To Date</Label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={fetchLedgerData}
                  className="bg-[#2C4C3B] hover:bg-[#1e3329]"
                >
                  Apply Filter
                </Button>
                {(fromDate || toDate) && (
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                  >
                    Clear
                  </Button>
                )}
            </div>
          </div>

            {/* Payment Summary for Filtered Period */}
            {(fromDate || toDate) && (
              <div className="mt-4 p-4 bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700 mb-1">
                      Total Payments {fromDate && toDate ? `(${fromDate} to ${toDate})` : fromDate ? `(from ${fromDate})` : `(up to ${toDate})`}
                    </p>
                    <p className="text-3xl font-bold text-green-900">₹{filteredPaymentTotal.toLocaleString()}</p>
                  </div>
                  <div className="text-green-600">
                    <Wallet size={48} weight="duotone" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Financial Summary Cards */}
        {ledgerSummary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-blue-700 mb-1">Total Purchases</p>
                  <p className="text-2xl font-bold text-blue-900">₹{(ledgerSummary.total_purchase_value || 0).toLocaleString()}</p>
                  <p className="text-xs text-blue-600 mt-1">Paid: ₹{(ledgerSummary.total_paid || 0).toLocaleString()}</p>
                </div>
                <Receipt size={32} className="text-blue-600" weight="duotone" />
              </div>
            </Card>

            <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-green-700 mb-1">Total Sales</p>
                  <p className="text-2xl font-bold text-green-900">₹{(ledgerSummary.total_sales_value || 0).toLocaleString()}</p>
                </div>
                <CurrencyDollar size={32} className="text-green-600" weight="duotone" />
              </div>
            </Card>

            <Card className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-orange-700 mb-1">Outstanding Payments</p>
                  <p className="text-2xl font-bold text-orange-900">₹{(ledgerSummary.outstanding_payments || 0).toLocaleString()}</p>
                </div>
                <Wallet size={32} className="text-orange-600" weight="duotone" />
              </div>
            </Card>

            <Card className={`p-4 bg-gradient-to-br ${ledgerSummary.profit_loss >= 0 ? 'from-purple-50 to-purple-100 border-purple-200' : 'from-red-50 to-red-100 border-red-200'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-sm ${ledgerSummary.profit_loss >= 0 ? 'text-purple-700' : 'text-red-700'} mb-1`}>Profit/Loss</p>
                  <p className={`text-2xl font-bold ${ledgerSummary.profit_loss >= 0 ? 'text-purple-900' : 'text-red-900'}`}>
                    ₹{Math.abs(ledgerSummary.profit_loss || 0).toLocaleString()}
                  </p>
                  <p className={`text-xs ${ledgerSummary.profit_loss >= 0 ? 'text-purple-600' : 'text-red-600'} mt-1`}>
                    Stock Value: ₹{(ledgerSummary.current_stock_value || 0).toLocaleString()}
                  </p>
                </div>
                {ledgerSummary.profit_loss >= 0 ? (
                  <TrendUp size={32} className="text-purple-600" weight="duotone" />
                ) : (
                  <TrendDown size={32} className="text-red-600" weight="duotone" />
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Purchases & Payments Section - Only visible for Admin and Owner */}
        {(userRole === 'admin' || userRole === 'owner') && (
          <Card>
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg">Purchases & Payments</h3>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => exportToExcel(purchases, 'purchases')}>
                    <FileXls size={16} className="mr-2" />
                    Export
                  </Button>
                  {ledgerSummary && ledgerSummary.outstanding_payments > 0 && (
                    <Button size="sm" variant="outline" onClick={openBulkPaymentDialog} className="bg-green-600 text-white hover:bg-green-700">
                      <Wallet size={16} className="mr-2" />
                      Pay Outstanding (₹{ledgerSummary.outstanding_payments.toLocaleString()})
                    </Button>
                  )}
                  <Button size="sm" onClick={() => setShowPurchaseDialog(true)} className="bg-[#2C4C3B]">
                    <Receipt size={16} className="mr-2" />
                    New Purchase
                  </Button>
                </div>
              </div>
            {transactions.length === 0 ? (
              <p className="text-center text-[#1C1C1A]/60 py-8">No transactions recorded yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#E5E5DF]">
                    <tr>
                      <th className="p-3 text-left">Type</th>
                      <th className="p-3 text-left">Description</th>
                      <th className="p-3 text-left">Supplier</th>
                      <th className="p-3 text-right">Qty</th>
                      <th className="p-3 text-right">Price/Unit</th>
                      <th className="p-3 text-right">Purchase</th>
                      <th className="p-3 text-right">Payment</th>
                      <th className="p-3 text-center">Payment Method</th>
                      <th className="p-3 text-right">Running Balance</th>
                      <th className="p-3 text-center">Date</th>
                      <th className="p-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((transaction, index) => {
                      const isPurchase = transaction.transaction_type === 'purchase';
                      const purchase = isPurchase ? purchases.find(p => p.purchase_id === transaction.purchase_id) : null;
                      
                      return (
                        <tr 
                          key={transaction.transaction_id} 
                          className={`border-b border-[#E5E5DF] hover:bg-[#F5F5F0] ${isPurchase ? 'bg-orange-50/30' : 'bg-green-50/30'}`}
                        >
                          <td className="p-3">
                            <Badge variant={isPurchase ? 'destructive' : 'success'}>
                              {isPurchase ? 'Purchase' : 'Payment'}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <div className="font-medium">
                              {isPurchase ? transaction.item_name : 'Bulk Payment'}
                            </div>
                            {transaction.remarks && (
                              <div className="text-xs text-[#1C1C1A]/60">{transaction.remarks}</div>
                            )}
                          </td>
                          <td className="p-3">
                            {isPurchase ? (transaction.supplier_name || '-') : '-'}
                          </td>
                          <td className="p-3 text-right">{transaction.quantity || '-'}</td>
                          <td className="p-3 text-right">
                            {transaction.price_per_unit ? `₹${transaction.price_per_unit.toLocaleString()}` : '-'}
                          </td>
                          <td className="p-3 text-right font-medium text-orange-700">
                            {transaction.purchase_amount > 0 ? `₹${transaction.purchase_amount.toLocaleString()}` : '-'}
                          </td>
                          <td className="p-3 text-right font-medium text-green-700">
                            {transaction.payment_amount > 0 ? `₹${transaction.payment_amount.toLocaleString()}` : '-'}
                          </td>
                          <td className="p-3 text-center">
                            {!isPurchase && transaction.payment_method ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                                {transaction.payment_method.toUpperCase()}
                              </Badge>
                            ) : '-'}
                          </td>
                          <td className="p-3 text-right font-bold">
                            <Badge variant={transaction.running_balance > 0 ? 'destructive' : 'success'}>
                              ₹{transaction.running_balance.toLocaleString()}
                            </Badge>
                          </td>
                          <td className="p-3 text-center text-xs">{transaction.date}</td>
                          <td className="p-3 text-center">
                            {isPurchase && purchase && (
                              <div className="flex gap-1 justify-center">
                                <Button size="sm" variant="outline" onClick={() => openEditPurchase(purchase)} className="text-blue-700 border-blue-700">
                                  Edit
                                </Button>
                                {(userRole === 'admin' || userRole === 'owner') && (
                                  <Button size="sm" variant="outline" onClick={() => openDeletePurchase(purchase)} className="text-red-700 border-red-700">
                                    Delete
                                  </Button>
                                )}
                              </div>
                            )}
                            {!isPurchase && (userRole === 'admin' || userRole === 'owner') && (
                              <div className="flex gap-1 justify-center">
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={fetchBulkPaymentHistory}
                                  className="text-blue-700 border-blue-700"
                                  title="View bulk payment history to edit or delete"
                                >
                                  View History
                                </Button>
                              </div>
                            )}
                            {!isPurchase && transaction.remarks && (userRole !== 'admin' && userRole !== 'owner') && (
                              <span className="text-xs text-[#1C1C1A]/60 italic">Bulk payment</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
        )}

        {/* Purchase Dialog */}
        <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Record Purchase</DialogTitle>
            </DialogHeader>
            <form onSubmit={handlePurchaseSubmit} className="space-y-4">
              <div>
                <Label>Barcode *</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    required
                    value={purchaseForm.barcode}
                    onChange={(e) => handleBarcodeChange(e.target.value)}
                    placeholder="Scan or enter barcode"
                    autoFocus
                    className="flex-1"
                  />
                  <BarcodeScanButton 
                    onScan={(code) => handleBarcodeChange(code)}
                    size="default"
                  />
                </div>
              </div>
              <div>
                <Label>Supplier Name</Label>
                <Input
                  value={purchaseForm.supplier_name}
                  onChange={(e) => setPurchaseForm({...purchaseForm, supplier_name: e.target.value})}
                  placeholder="Enter supplier name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    required
                    min="1"
                    value={purchaseForm.quantity}
                    onChange={(e) => setPurchaseForm({...purchaseForm, quantity: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Price per Unit *</Label>
                  <Input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={purchaseForm.price_per_unit}
                    onChange={(e) => setPurchaseForm({...purchaseForm, price_per_unit: parseFloat(e.target.value)})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Amount Paid (Initial)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={purchaseForm.paid_amount}
                    onChange={(e) => setPurchaseForm({...purchaseForm, paid_amount: parseFloat(e.target.value)})}
                    placeholder="Enter amount paid"
                  />
                </div>
                <div>
                  <Label>Payment Method</Label>
                  <Select value={purchaseForm.initial_payment_method} onValueChange={(val) => setPurchaseForm({...purchaseForm, initial_payment_method: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(method => (
                        <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="p-3 bg-[#F5F5F0] rounded">
                <div className="flex justify-between text-sm">
                  <span>Total Amount:</span>
                  <span className="font-bold">₹{(purchaseForm.quantity * purchaseForm.price_per_unit).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span>Remaining Balance:</span>
                  <span className="font-bold text-orange-700">
                    ₹{((purchaseForm.quantity * purchaseForm.price_per_unit) - purchaseForm.paid_amount).toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowPurchaseDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#2C4C3B]">
                  Record Purchase
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Payment Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Add Payment</DialogTitle>
              {selectedPurchase && (
                <div className="mt-2 text-sm">
                  <p className="text-[#1C1C1A]/70">Purchase: {selectedPurchase.item_name}</p>
                  <p className="text-orange-700 font-medium">Remaining Balance: ₹{(selectedPurchase.remaining_balance || 0).toLocaleString()}</p>
                </div>
              )}
            </DialogHeader>
            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Amount *</Label>
                  <Input
                    type="number"
                    required
                    min="0.01"
                    step="0.01"
                    max={selectedPurchase?.remaining_balance}
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value)})}
                    placeholder="Enter payment amount"
                    autoFocus
                  />
                </div>
                <div>
                  <Label>Payment Method *</Label>
                  <Select 
                    value={paymentForm.payment_method} 
                    onValueChange={(val) => setPaymentForm({...paymentForm, payment_method: val})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(method => (
                        <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Remarks</Label>
                <Input
                  value={paymentForm.remarks}
                  onChange={(e) => setPaymentForm({...paymentForm, remarks: e.target.value})}
                  placeholder="Optional payment notes"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#2C4C3B]">
                  Record Payment
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Payment Dialog */}
        <Dialog open={showBulkPaymentDialog} onOpenChange={setShowBulkPaymentDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Pay Outstanding Balance</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleBulkPaymentSubmit} className="space-y-4">
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800 mb-2">
                  Total Outstanding: <span className="font-bold text-lg">₹{ledgerSummary?.outstanding_payments.toLocaleString()}</span>
                </p>
                <p className="text-xs text-orange-700">
                  This payment will be distributed across all purchases with pending balances
                </p>
              </div>

              {availableCredit > 0 && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-green-800 font-medium">
                      💰 Available Credit: <span className="font-bold text-lg">₹{availableCredit.toLocaleString()}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="use_credit"
                      checked={bulkPaymentForm.use_credit}
                      onChange={(e) => {
                        const useCredit = e.target.checked;
                        setBulkPaymentForm(prev => ({
                          ...prev,
                          use_credit: useCredit,
                          amount: useCredit ? Math.max(0, ledgerSummary?.outstanding_payments - availableCredit) : ledgerSummary?.outstanding_payments
                        }));
                      }}
                      className="w-4 h-4 text-green-600 rounded"
                    />
                    <label htmlFor="use_credit" className="text-sm text-green-700 cursor-pointer">
                      Use available credit (You&apos;ll pay ₹{Math.max(0, (ledgerSummary?.outstanding_payments || 0) - availableCredit).toLocaleString()} cash)
                    </label>
                  </div>
                </div>
              )}

              <div>
                <Label>Cash Payment Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  required
                  value={bulkPaymentForm.amount}
                  onChange={(e) => setBulkPaymentForm({...bulkPaymentForm, amount: e.target.value})}
                  placeholder="Enter amount"
                  disabled={bulkPaymentForm.use_credit && availableCredit >= ledgerSummary?.outstanding_payments}
                />
                {bulkPaymentForm.use_credit && (
                  <p className="text-xs text-green-600 mt-1">
                    Total: ₹{(parseFloat(bulkPaymentForm.amount) + availableCredit).toLocaleString()} (Cash: ₹{bulkPaymentForm.amount.toLocaleString()} + Credit: ₹{availableCredit.toLocaleString()})
                  </p>
                )}
              </div>

              <div>
                <Label>Payment Method *</Label>
                <select
                  required
                  value={bulkPaymentForm.payment_method}
                  onChange={(e) => setBulkPaymentForm({...bulkPaymentForm, payment_method: e.target.value})}
                  className="w-full p-2 border border-[#E5E5DF] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]"
                >
                  {PAYMENT_METHODS.map(method => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Remarks</Label>
                <Input
                  value={bulkPaymentForm.remarks}
                  onChange={(e) => setBulkPaymentForm({...bulkPaymentForm, remarks: e.target.value})}
                  placeholder="Optional notes"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowBulkPaymentDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                >
                  Record Payment
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Payment History Dialog */}
        <Dialog open={showBulkPaymentHistoryDialog} onOpenChange={setShowBulkPaymentHistoryDialog}>
          <DialogContent className="bg-white max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Bulk Payment History</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {bulkPaymentHistory.length === 0 ? (
                <p className="text-center text-[#1C1C1A]/60 py-8">No bulk payments recorded yet</p>
              ) : (
                bulkPaymentHistory.map((payment, index) => (
                  <Card key={payment.bulk_payment_id} className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <p className="font-semibold text-lg text-[#2C4C3B]">
                          ₹{payment.total_amount.toLocaleString()} - {payment.payment_method.toUpperCase()}
                        </p>
                        <p className="text-xs text-[#1C1C1A]/60">
                          {new Date(payment.created_at).toLocaleString()} • By {payment.created_by_name}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          {payment.purchases_count} Purchase{payment.purchases_count > 1 ? 's' : ''} Affected
                        </Badge>
                        {userRole === 'admin' && (
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => {
                                setSelectedBulkPayment(payment);
                                const paymentDate = payment.created_at ? payment.created_at.split('T')[0] : new Date().toISOString().split('T')[0];
                                setBulkPaymentForm({
                                  amount: payment.total_amount || 0,
                                  payment_method: payment.payment_method || 'cash',
                                  remarks: payment.remarks || '',
                                  date: paymentDate
                                });
                                setShowEditBulkPaymentDialog(true);
                                setShowBulkPaymentHistoryDialog(false);
                              }}
                              className="text-blue-700 border-blue-700 hover:bg-blue-50"
                            >
                              Edit
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => {
                                setBulkPaymentToDelete(payment);
                                setShowDeleteBulkPaymentDialog(true);
                                setShowBulkPaymentHistoryDialog(false);
                              }}
                              className="text-red-700 border-red-700 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {payment.remarks && (
                      <p className="text-sm text-[#1C1C1A]/70 mb-3 italic">&ldquo;{payment.remarks}&rdquo;</p>
                    )}
                    
                    <div className="grid grid-cols-3 gap-4 mb-3 text-sm">
                      <div>
                        <p className="text-[#1C1C1A]/60">Total Paid</p>
                        <p className="font-medium text-green-700">₹{payment.total_amount.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-[#1C1C1A]/60">Distributed</p>
                        <p className="font-medium">₹{payment.distributed_amount.toLocaleString()}</p>
                      </div>
                      {payment.remaining_amount > 0 && (
                        <div>
                          <p className="text-[#1C1C1A]/60">Excess Amount</p>
                          <p className="font-medium text-orange-700">₹{payment.remaining_amount.toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                    
                    <details className="mt-3">
                      <summary className="cursor-pointer text-sm font-medium text-[#2C4C3B] hover:underline">
                        View Distribution Details ({payment.purchases_count} purchases)
                      </summary>
                      <div className="mt-2 overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-[#F5F5F0]">
                            <tr>
                              <th className="p-2 text-left">Item</th>
                              <th className="p-2 text-left">Supplier</th>
                              <th className="p-2 text-right">Previous Balance</th>
                              <th className="p-2 text-right">Payment Applied</th>
                              <th className="p-2 text-right">Balance After</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payment.purchases_affected?.map((purchase, idx) => (
                              <tr key={idx} className="border-b">
                                <td className="p-2">{purchase.item_name}</td>
                                <td className="p-2">{purchase.supplier}</td>
                                <td className="p-2 text-right">₹{purchase.previous_balance?.toLocaleString()}</td>
                                <td className="p-2 text-right text-green-700 font-medium">₹{purchase.payment_applied.toLocaleString()}</td>
                                <td className="p-2 text-right">
                                  <Badge variant={purchase.remaining_after > 0 ? 'destructive' : 'success'}>
                                    ₹{purchase.remaining_after.toLocaleString()}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  </Card>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Purchase Dialog */}
        <Dialog open={showEditPurchaseDialog} onOpenChange={setShowEditPurchaseDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-blue-700">Edit Purchase</DialogTitle>
            </DialogHeader>
            {selectedPurchase && (
              <form onSubmit={handleEditPurchaseSubmit} className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium">{selectedPurchase.item_name}</p>
                  <p className="text-xs text-blue-700">Barcode: {selectedPurchase.barcode}</p>
                </div>

                <div>
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    required
                    min="0.01"
                    step="0.01"
                    value={purchaseForm.quantity}
                    onChange={(e) => setPurchaseForm({...purchaseForm, quantity: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Price per Unit *</Label>
                  <Input
                    type="number"
                    required
                    min="0.01"
                    step="0.01"
                    value={purchaseForm.price_per_unit}
                    onChange={(e) => setPurchaseForm({...purchaseForm, price_per_unit: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Supplier Name</Label>
                  <Input
                    value={purchaseForm.supplier_name}
                    onChange={(e) => setPurchaseForm({...purchaseForm, supplier_name: e.target.value})}
                    placeholder="Optional"
                  />
                </div>

                <div>
                  <Label>Purchase Date *</Label>
                  <Input
                    type="date"
                    required
                    value={purchaseForm.purchase_date}
                    onChange={(e) => setPurchaseForm({...purchaseForm, purchase_date: e.target.value})}
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowEditPurchaseDialog(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-blue-700 hover:bg-blue-800">
                    Update Purchase
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Purchase Dialog */}
        <Dialog open={showDeletePurchaseDialog} onOpenChange={setShowDeletePurchaseDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-red-700">Delete Purchase</DialogTitle>
            </DialogHeader>
            {purchaseToDelete && (
              <div className="space-y-4">
                <p className="text-[#1C1C1A]/80">
                  Are you sure you want to delete this purchase?
                </p>
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <p className="text-sm font-medium">Item: {purchaseToDelete.item_name}</p>
                  <p className="text-sm">Barcode: {purchaseToDelete.barcode}</p>
                  <p className="text-sm">Supplier: {purchaseToDelete.supplier_name || 'N/A'}</p>
                  <p className="text-sm">Amount: ₹{(purchaseToDelete.amount_total || 0).toLocaleString()}</p>
                  <p className="text-sm">Date: {purchaseToDelete.purchase_date}</p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                  <p className="text-xs text-orange-900">
                    ⚠️ Stock will be removed from inventory after deletion.
                  </p>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowDeletePurchaseDialog(false);
                      setPurchaseToDelete(null);
                    }} 
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleDeletePurchase} 
                    className="flex-1 bg-red-700 hover:bg-red-800"
                  >
                    Delete Purchase
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Bulk Payment Dialog */}
        <Dialog open={showEditBulkPaymentDialog} onOpenChange={setShowEditBulkPaymentDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-blue-700">Edit Bulk Payment</DialogTitle>
            </DialogHeader>
            {selectedBulkPayment && (
              <form onSubmit={handleEditBulkPaymentSubmit} className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium">Bulk Payment</p>
                  <p className="text-xs text-blue-700">ID: {selectedBulkPayment.bulk_payment_id}</p>
                </div>

                <div>
                  <Label>Payment Date *</Label>
                  <Input
                    type="date"
                    required
                    value={bulkPaymentForm.date}
                    onChange={(e) => setBulkPaymentForm({...bulkPaymentForm, date: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Payment Amount *</Label>
                  <Input
                    type="number"
                    required
                    min="0.01"
                    step="0.01"
                    value={bulkPaymentForm.amount}
                    onChange={(e) => setBulkPaymentForm({...bulkPaymentForm, amount: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Payment Method *</Label>
                  <select
                    required
                    value={bulkPaymentForm.payment_method}
                    onChange={(e) => setBulkPaymentForm({...bulkPaymentForm, payment_method: e.target.value})}
                    className="w-full p-2 border border-[#E5E5DF] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]"
                  >
                    {PAYMENT_METHODS.map(method => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Remarks</Label>
                  <Input
                    value={bulkPaymentForm.remarks}
                    onChange={(e) => setBulkPaymentForm({...bulkPaymentForm, remarks: e.target.value})}
                    placeholder="Optional notes"
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowEditBulkPaymentDialog(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-blue-700 hover:bg-blue-800">
                    Update Payment
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Bulk Payment Dialog */}
        <Dialog open={showDeleteBulkPaymentDialog} onOpenChange={setShowDeleteBulkPaymentDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-red-700">Delete Bulk Payment</DialogTitle>
            </DialogHeader>
            {bulkPaymentToDelete && (
              <div className="space-y-4">
                <p className="text-[#1C1C1A]/80">
                  Are you sure you want to delete this bulk payment?
                </p>
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <p className="text-sm font-medium">Amount: ₹{(bulkPaymentToDelete.payment_amount || 0).toLocaleString()}</p>
                  <p className="text-sm">Payment Method: {bulkPaymentToDelete.payment_method?.toUpperCase()}</p>
                  <p className="text-sm">Date: {bulkPaymentToDelete.date}</p>
                  {bulkPaymentToDelete.remarks && (
                    <p className="text-sm">Remarks: {bulkPaymentToDelete.remarks}</p>
                  )}
                </div>
                <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                  <p className="text-xs text-orange-900">
                    ⚠️ This action cannot be undone. Outstanding balances will be recalculated.
                  </p>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowDeleteBulkPaymentDialog(false);
                      setBulkPaymentToDelete(null);
                    }} 
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleDeleteBulkPayment} 
                    className="flex-1 bg-red-700 hover:bg-red-800"
                  >
                    Delete Payment
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CompanyLedger;
